// src/utils/dentallyPatientSummary.js
//
// Normalises the `patient_summary` object on GET
// /practices/{p}/leads/{l}/dentally/sync/ into the shape the Dentally patient
// card renders.
//
// Contract fixed by the backend on 2026-09-20. The whole object is null in
// three cases — the lead is not linked, the integration is inactive or absent,
// or the provider read failed — so it must never be inferred from `linked`
// alone. When it is non-null all eight keys are present, already trimmed, with
// Dentally's placeholder strings ("N/A", "-", "none"…) already nulled out.
//
// Two fields are TRI-STATE, and collapsing either to a boolean would state
// something the provider did not: `medical_alert` null means Dentally did not
// tell us, which is not "no alert", and `active` null is not "archived".
import { siteFromConfig } from './dentallySite';

/** Shown when Dentally gave a site the practice's config cannot name. */
export const UNNAMED_SITE_LABEL = 'Other site';

/**
 * `archived_reason` is an enum TOKEN, not prose — the backend passes Dentally's
 * value through verbatim and deliberately does not allow-list it. Rendering the
 * raw value would show staff the literal string "no_reason_given".
 *
 * Deliberately NOT an allow-list: a token we have not seen is still worth
 * showing, so anything unmapped is humanised rather than dropped. `deceased`
 * must never be the value a strict list swallows.
 */
export const ARCHIVE_REASON_LABELS = Object.freeze({
  no_reason_given: 'No reason given',
  deceased: 'Deceased',
  lapsed: 'Lapsed',
  duplicate: 'Duplicate record',
});

/** Label for an archive-reason token; unknown tokens are humanised, never dropped. */
export function formatArchiveReason(token) {
  if (typeof token !== 'string' || !token.trim()) return null;
  const key = token.trim().toLowerCase();
  if (ARCHIVE_REASON_LABELS[key]) return ARCHIVE_REASON_LABELS[key];
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** True for the one archive reason with a real-world consequence before a phone call. */
export function isDeceasedReason(token) {
  return typeof token === 'string' && token.trim().toLowerCase() === 'deceased';
}

/**
 * Conventional 3-3-4 spacing for a ten-digit NHS number. Display only, and only
 * for a value that is exactly ten digits — anything else is shown exactly as
 * Dentally holds it rather than reshaped into a format it may not be in.
 *
 * Deliberately conditional. Dentally documents `nhs_number` as a bare optional
 * string with no format, length or character set, and there is not one non-null
 * value anywhere in the staging account, so the real shape has never been
 * observed. A value that already arrives spaced fails the ten-digit test and
 * passes through untouched; an unconditional strip-then-reformat would rewrite
 * a format we have never seen.
 */
export function formatNhsNumber(value) {
  if (!value) return null;
  const digits = value.replace(/\s/g, '');
  if (!/^\d{10}$/.test(digits)) return value;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

const asText = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

// `active` is the one field where an unknown value must not read as "archived":
// a missing flag means the provider did not tell us, not that the patient is
// gone. Only an explicit false is treated as archived.
const asTriState = (value) => {
  if (value === true) return true;
  if (value === false) return false;
  return null;
};

/**
 * @returns {null|{
 *   dentallyUrl: string|null,
 *   medicalAlert: boolean|null,
 *   medicalAlertText: string|null,
 *   site: {id: string, name: string}|null,
 *   active: boolean|null,
 *   archivedReason: string|null,
 *   archivedReasonLabel: string|null,
 *   archivedReasonIsDeceased: boolean,
 *   nhsNumber: string|null,
 *   preferredName: string|null,
 *   hasContent: boolean,
 * }}
 */
export function normalisePatientSummary(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const medicalAlertText = asText(raw.medical_alert_text);
  // A practice can flag an alert without typing one, and text can arrive with
  // the flag unset or unknown — either means there IS an alert to show. The
  // third state survives: flag null with no text means "not reported", which
  // the card says out loud rather than passing off as an all-clear.
  const flag = asTriState(raw.medical_alert);
  let medicalAlert = flag === true || Boolean(medicalAlertText);
  if (!medicalAlert && flag === null) medicalAlert = null;

  // Accepts the contract's nested `site: { id, name }` and the flat
  // `site_id` / `site_name` spelling the other Dentally serialisers use.
  // `name` is legitimately null when the patient sits outside the practice's
  // configured site — siteFromConfig falls back to the id, which is a bare
  // UUID and useless to a receptionist, so say "Other site" instead.
  const rawSite = siteFromConfig(raw);
  const siteNamed = typeof (raw.site?.name ?? raw.site_name) === 'string'
    && String(raw.site?.name ?? raw.site_name).trim() !== '';
  const site = rawSite && { ...rawSite, name: siteNamed ? rawSite.name : UNNAMED_SITE_LABEL };

  const active = asTriState(raw.active);

  const summary = {
    dentallyUrl: asText(raw.dentally_url),
    medicalAlert,
    medicalAlertText,
    site,
    active,
    // Independent of `active`, on the backend's instruction: Dentally can send a
    // reason with `active` true, or `active` false with no reason. So the reason
    // is never hidden because the patient looks active, and archived-ness is
    // never inferred from a reason being present — only `active === false` does
    // that (see the chip modifier in DentallyPatientLinkSection).
    archivedReason: asText(raw.archived_reason),
    archivedReasonLabel: formatArchiveReason(raw.archived_reason),
    archivedReasonIsDeceased: isDeceasedReason(raw.archived_reason),
    nhsNumber: formatNhsNumber(asText(raw.nhs_number)),
    preferredName: asText(raw.preferred_name),
  };

  // An object where every field came back null renders nothing at all, rather
  // than an empty heading with no rows under it. An unreported medical alert
  // (`medicalAlert === null`) is not content on its own — a summary that told
  // us nothing at all should not announce that one absence.
  summary.hasContent = Boolean(
    summary.dentallyUrl
    || summary.medicalAlert === true
    || summary.site
    || summary.active === false
    || summary.archivedReason
    || summary.nhsNumber
    || summary.preferredName,
  );

  return summary;
}

/**
 * Sentence for an archived Dentally patient, with the provider's reason when it
 * gave one. Only `active === false` counts as archived; a reason on its own
 * never does, and an unknown `active` never does.
 */
export function archivedPatientMessage(summary) {
  if (!summary || summary.active !== false) return null;
  // "no_reason_given" adds nothing to the sentence, and a deceased patient gets
  // its own notice above rather than a parenthetical here.
  const label = summary.archivedReasonIsDeceased || summary.archivedReason === 'no_reason_given'
    ? null
    : summary.archivedReasonLabel;
  return label
    ? `This patient is archived in Dentally (${label}).`
    : 'This patient is archived in Dentally.';
}

/** Sentence for a patient Dentally records as deceased, whatever `active` says. */
export function deceasedPatientMessage(summary) {
  if (!summary?.archivedReasonIsDeceased) return null;
  return 'This patient is recorded as deceased in Dentally.';
}

export default normalisePatientSummary;
